/**
 * API Services
 * Centralized export for all API services
 */

import { ExerciseService } from './ExerciseService';
import { TemplateService } from './TemplateService';
import { WorkoutHistoryService } from './WorkoutHistoryService';
import { WorkoutSessionService } from './WorkoutSessionService';
import { UserService } from './UserService';
import { FeedbackService } from './FeedbackService';
import { I18nService } from './I18nService';
import { CacheManager } from '../cache/CacheManager';

// Create service instances
const exerciseService = new ExerciseService();
const workoutHistoryService = new WorkoutHistoryService();

/**
 * Singleton instances of all services
 * Use these throughout the application for consistent state management
 */
export const apiServices = {
  exercises: exerciseService,
  templates: new TemplateService(),
  workoutHistory: workoutHistoryService,
  workoutSession: new WorkoutSessionService(),
  users: new UserService(),
  feedback: new FeedbackService(),
  i18n: new I18nService(),
  // Cache management methods
  cache: {
    // Exercise cache methods
    clearExerciseCache: () => exerciseService.clearCache(),
    getExerciseCacheStats: () => exerciseService.getCacheStats(),
    // Max weight cache methods
    clearMaxWeightCache: () => workoutHistoryService.clearMaxWeightCache(),
    clearMaxWeightCacheForUser: (userId: string) => workoutHistoryService.clearMaxWeightCacheForUser(userId),
    // Progress analytics cache methods
    clearProgressAnalyticsCache: () => workoutHistoryService.clearProgressAnalyticsCache(),
    clearProgressAnalyticsCacheForUser: (userId: string) => workoutHistoryService.clearProgressAnalyticsCacheForUser(userId),
    // All cache methods
    clearAllCaches: () => workoutHistoryService.clearAllCaches(),
    clearAllCachesForUser: (userId: string) => workoutHistoryService.clearAllCachesForUser(userId),
    // Cache stats
    getMaxWeightCacheStats: () => workoutHistoryService.getMaxWeightCacheStats(),
    getWorkoutCacheStats: () => workoutHistoryService.getCacheStats(),

    // Global cache configuration
    isCacheEnabled: () => CacheManager.isCacheEnabled(),
  }
};

if (process.env.NODE_ENV === 'development') {
  (window as any).gymPlannerCache = {
    isCacheEnabled: () => CacheManager.isCacheEnabled(),
    clearAll: () => {
      apiServices.cache.clearExerciseCache();
      apiServices.cache.clearAllCaches();
      // Feedback cache is invalidated on mutation; provide this hook for dev convenience
      console.log('🧹 Cleared API service caches');
    },
  };
}

// Export individual services for type imports
export { ExerciseService } from './ExerciseService';
export { TemplateService } from './TemplateService';
export { WorkoutHistoryService } from './WorkoutHistoryService';
export { WorkoutSessionService } from './WorkoutSessionService';
export { UserService } from './UserService';
export { FeedbackService } from './FeedbackService';
export { I18nService } from './I18nService';
export { BaseApiService } from './BaseApiService';
